import type { TourSection } from './types';

// Helper que clickea el botón del tab antes de mostrar el step. Joyride v3
// `before` es async — esperamos al click + frames para que React renderee
// el Card del tab antes de buscar el target.
function openTab(key: string): () => Promise<void> {
  return async () => {
    const btn = document.querySelector<HTMLButtonElement>(
      `[data-tour="product-tab-${key}"]`,
    );
    if (btn && btn.getAttribute('aria-selected') !== 'true') {
      btn.click();
      // Espera 2 frames para que el panel se monte tras setState + URL update.
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    }
  };
}

// El detalle de producto tiene 6 pestañas. Antes de cada step, abrimos
// automáticamente el tab correspondiente — así el "Tour completo" funciona
// aunque el user arranque en cualquier tab.

export const productsDetailSections: TourSection[] = [
  {
    id: 'tab-general',
    label: 'Tab General',
    steps: [
      {
        target: '[data-tour="product-detail-general"]',
        title: 'Datos generales',
        content:
          'Información principal del producto: SKU (no editable), nombre, marca, categoría y si está activo o no. Si una regla de activación apagó el producto, la casilla de activo queda bloqueada y debajo aparece qué regla lo apagó y desde cuándo; se vuelve a prender sola cuando el producto cumpla las reglas. Al guardar, si hay clientes externos suscriptos, reciben aviso automático del cambio.',
        before: openTab('general'),
      },
    ],
  },
  {
    id: 'tab-attributes',
    label: 'Tab Atributos',
    steps: [
      {
        target: '[data-tour="product-detail-attributes"]',
        title: 'Atributos del producto',
        content:
          'Características adicionales: descripciones, especificaciones, dimensiones, etc. Los campos que tienen configurada inteligencia artificial muestran un botón Gemini para generar el valor automáticamente — la sugerencia queda en la bandeja de Sugerencias IA para que la revises antes de aplicarla. "Modelos compatibles" usa un buscador contra el catálogo de Modelos vehículos — si algún modelo aparece en rojo abajo significa que no está en ese catálogo y no se puede garantizar en integraciones. Si una regla de activación apagó "Activo en web", ese campo queda bloqueado con el motivo debajo.',
        before: openTab('attributes'),
      },
    ],
  },
  {
    id: 'tab-media',
    label: 'Tab Imágenes',
    steps: [
      {
        target: '[data-tour="product-detail-media"]',
        title: 'Galería de imágenes',
        content:
          'Carga imágenes arrastrándolas o desde el botón. Puedes marcar una como principal (la que se ve en el listado), editar su texto alternativo, reordenarlas y eliminarlas.',
        before: openTab('media'),
      },
    ],
  },
  {
    id: 'tab-metadata',
    label: 'Tab Metadata',
    steps: [
      {
        target: '[data-tour="product-detail-metadata"]',
        title: 'Metadata SEO',
        content:
          'Campos de SEO y metadata del producto (metaTitle, metaDescription, etc.). Mismo formato que Atributos — el backend define qué campos existen. Se editan igual que los atributos y se guardan con "Guardar metadata".',
        before: openTab('metadata'),
      },
    ],
  },
  {
    id: 'tab-versions',
    label: 'Tab Versiones',
    steps: [
      {
        target: '[data-tour="product-detail-versions"]',
        title: 'Historial de versiones',
        content:
          'Cada cambio guardado del producto se conserva como una versión. Sirve para revisar cómo era antes y, si hace falta, comparar contra el actual.',
        before: openTab('versions'),
      },
    ],
  },
  {
    id: 'tab-audit',
    label: 'Tab Auditoría',
    steps: [
      {
        target: '[data-tour="product-detail-audit"]',
        title: 'Quién hizo qué',
        content:
          'Lista de todas las acciones sobre este producto: quién lo creó, quién lo modificó, qué cambió y cuándo. Útil para investigar cuando algo se editó por error.',
        before: openTab('audit'),
      },
    ],
  },
  {
    id: 'tab-ai',
    label: 'Tab Sugerencias IA',
    steps: [
      {
        target: '[data-tour="product-detail-ai"]',
        title: 'Sugerencias de IA del producto',
        content:
          'Solo las sugerencias generadas por IA para este producto en particular. Aceptar aplica el valor al producto; rechazar las descarta sin tocarlo.',
        before: openTab('ai'),
      },
    ],
  },
];
